// Type what to buy. Shows where it'll go (tap to change before adding), "Did you mean …?" for
// likely typos, and things the household usually buys (each with a Forget button).
import { useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { itemKey, parseEntry, suggest, suggestCorrection, type Memory } from '@milk/shared';
import { addItem, forgetMemory, getState, guessCategory, useStore } from '../store/store';
import { useToast } from './ui';
import { SectionPicker } from './SectionPicker';

export function AddBar({ storeId, onAdded }: { storeId: string | null; onAdded?(id: string): void }) {
  const [text, setText] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [onlyHere, setOnlyHere] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const toast = useToast();
  const memories = useStore((s) => s.memories);
  const items = useStore((s) => s.items);
  const categories = useStore((s) => s.categories);
  const storeName = useStore((s) => (storeId ? s.stores[storeId]?.name : null));

  const name = parseEntry(text).name;
  const trimmed = name.trim();
  const onList = useMemo(() => new Set(Object.values(items).filter((i) => !i.checked).map((i) => itemKey(i.name))), [items]);
  const suggestions = useMemo(() => (trimmed ? suggest(trimmed, Object.values(memories), { exclude: onList, limit: 6 }) : []), [trimmed, memories, onList]);
  // only when nothing remembered matches: "banan" is someone still typing "bananas"
  const correction = useMemo(() => (trimmed.length >= 4 && !suggestions.length ? suggestCorrection(trimmed, Object.values(memories)) : null), [trimmed, suggestions.length, memories]);
  const categoryId = chosen ?? (trimmed ? guessCategory(trimmed) : null);
  const category = categoryId ? categories[categoryId] : null;

  const add = (what: string, opts: { categoryId?: string | null } = {}) => {
    const r = addItem(what, { categoryId: opts.categoryId ?? undefined, storeIds: storeId && onlyHere ? [storeId] : [] });
    if (!r) return;
    const where = getState().categories[r.item.categoryId]?.name ?? '';
    toast(r.merged ? `${r.item.name} is already on the list${r.item.quantity ? ` (${r.item.quantity})` : ''}` : `Added ${r.item.name}${where ? ` to ${where}` : ''}`);
    onAdded?.(r.item.id);
    setText('');
    setChosen(null);
    input.current?.focus();
  };

  const pickSuggestion = (m: Memory) => {
    const { quantity } = parseEntry(text);
    add(quantity ? `${quantity} ${m.name}` : m.name);
  };

  return (
    <div className="add">
      <form onSubmit={(e) => { e.preventDefault(); if (trimmed) add(text, { categoryId: chosen }); }} role="search" aria-label="Add to the list">
        <label htmlFor="add-input" className="sr-only">Add an item</label>
        <input
          id="add-input"
          ref={input}
          value={text}
          onChange={(e) => { setText(e.target.value); setChosen(null); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && suggestions.length) { e.preventDefault(); listRef.current?.querySelector<HTMLButtonElement>('.pick')?.focus(); }
            if (e.key === 'Escape') setText('');
          }}
          placeholder="Add milk, maybe?"
          autoComplete="off"
          autoCapitalize="sentences"
          enterKeyHint="done"
          aria-describedby="add-hints"
        />
        <button className="btn primary" disabled={!trimmed} aria-label="Add"><Plus size={20} aria-hidden /><span className="hide-sm">Add</span></button>
      </form>

      <div className="add-hints" id="add-hints" aria-live="polite">
        {category && (
          <button type="button" className="goes-to" onClick={() => setPicking(true)} aria-label={`Goes in ${category.name}. Change section`}>
            <span aria-hidden>{category.emoji}</span> {category.name}
          </button>
        )}
        {correction && (
          <span className="did-you-mean">
            Did you mean <button type="button" className="link" onClick={() => add(correction, { categoryId: null })}>{correction}</button>?
          </span>
        )}
        {trimmed && storeId && storeName && (
          <label className="switch small"><input type="checkbox" checked={onlyHere} onChange={(e) => setOnlyHere(e.target.checked)} /> Only at {storeName}</label>
        )}
      </div>

      {suggestions.length > 0 && (
        <ul className="suggestions" ref={listRef} aria-label="Things you usually buy"
          onKeyDown={(e) => {
            const picks = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('.pick') ?? [])];
            const i = picks.indexOf(document.activeElement as HTMLButtonElement);
            if (e.key === 'ArrowDown' && i >= 0) { e.preventDefault(); picks[Math.min(i + 1, picks.length - 1)]?.focus(); }
            if (e.key === 'ArrowUp' && i >= 0) { e.preventDefault(); if (i === 0) input.current?.focus(); else picks[i - 1]?.focus(); }
            if (e.key === 'Escape') { input.current?.focus(); }
          }}>
          {suggestions.map(({ memory: m }) => {
            const cat = categories[m.categoryId ?? guessCategory(m.name)];
            return (
              <li key={m.key}>
                <button type="button" className="pick" onClick={() => pickSuggestion(m)}>
                  <span>{m.name}</span>
                  {cat && <span className="where">{cat.emoji} {cat.name}</span>}
                </button>
                <button type="button" className="btn ghost forget" onClick={() => { forgetMemory(m.key); toast(`Forgot ${m.name}`); input.current?.focus(); }} aria-label={`Forget ${m.name}`}>
                  Forget
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {picking && (
        <SectionPicker
          title={`Where does ${trimmed || 'it'} go?`}
          current={categoryId}
          storeId={storeId}
          onPick={(id) => { setChosen(id); setPicking(false); requestAnimationFrame(() => input.current?.focus()); }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}
